import './AssignGroupOverlay.scss';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router';
import { BRAND_LETTER } from '../../js/config/_global';

import { useTranslation } from 'react-i18next';
import {
    RESPONSE,
    ROUTES,
    SEARCH_DEBOUNCE_VALUE
} from '../../js/_constants';
import Helpers, {
    sanitizeCollectionResponse
} from '../../js/helpers';
import { type Playlist, type Device, PlaylistGroup } from '../../api/types/index';

import InputField from '../inputField/InputField';
import ItemCard from '../ItemCard/ItemCard';
import { type CollectionResponse } from '../../api/types/response';
import { useFetch } from '../../hooks/useFetch';
import _server from '../../js/config/_server';
import { type SanitizedCollection } from '../../js/helpers';
import Loader from '../loader/Loader';
import debounce from 'debounce';
import { isEmpty } from 'ramda';

type AssignGroupOverlayProps = {
    isFilterable: boolean;
    onItemClick: (playlistIri: string) => void;
    selectedGroups: any[];
};

type SanitizerFunction = (
    collection: CollectionResponse<Playlist>
) => SanitizedCollection<Playlist>;

const AssignGroupOverlay = ({
    isFilterable,
    onItemClick,
    selectedGroups = [],
}: AssignGroupOverlayProps) => {
    const { t } = useTranslation();

    const [pendingIri, setPendingIri] = useState('');

    const {
        data: groups,
        isLoading,
        refetchData
    } = useFetch<CollectionResponse<PlaylistGroup>, SanitizerFunction>(
        _server.services.playlistGroups.getAll,
        {
            params: {
                brand: BRAND_LETTER,
            },
            dataFormatter: (collection: CollectionResponse<Playlist>) =>
                sanitizeCollectionResponse<Playlist>(collection)
        }
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        refetchData({
            params: {
                ...(value && {title: value})
            },
            dataFormatter: (collection: CollectionResponse<Playlist>) =>
                sanitizeCollectionResponse<Playlist>(collection)
        });
    };

    const debouncedHandleSearch = debounce(handleSearch, SEARCH_DEBOUNCE_VALUE);

    const handleAddGroupToDevice = playlistIri => onItemClick(playlistIri);

    const isSelected = item =>
        selectedGroups.findIndex(
            selectedGroup =>
                selectedGroup[RESPONSE.ID] === item[RESPONSE.ID]
        ) > -1;

    const handleGroupAssignment = (itemIri: string) => {
        setPendingIri(itemIri);
        handleAddGroupToDevice(itemIri);
    };

    const handleGroupCardClick = (GroupId: string) => {
        handleGroupAssignment(GroupId);
    };

    useEffect(() => {
        setPendingIri('');
    }, [selectedGroups]);

    const renderGroups = () => {
        if (isEmpty(groups?.list)) {
            return (
                <span className="empty-groups">
                    {t('device_detail_playlist_overlay_no_playlists')}
                </span>
            );
        }
        // @ts-ignore
        return groups?.list.map(item => {
            const itemClasses = classNames({
                'is-selected': isSelected(item),
                'group-card': true
            });
            return (
                <li key={item[RESPONSE.ID]}>
                    <ItemCard
                        key={item[RESPONSE.ID]}
                        className={itemClasses}
                        icon={isSelected(item) ? "select-circle" : "media-playlist" }
                        title={item.title}
                        isLoading={pendingIri === item[RESPONSE.ID]}
                        onClick={() =>
                            handleGroupCardClick(item[RESPONSE.ID])
                        }
                    />
                </li>
            );
        });
    };

    return (
        <div
            className={classNames('b-assign-group-overlay', {
                'is-loading': !!pendingIri
            })}
        >
            {isFilterable && (
                <InputField
                    label={t('searchInput')}
                    name="searchInput"
                    onChangeHandler={debouncedHandleSearch}
                />
            )}
            <ul className="groups-list">
                {isLoading ? <Loader /> : renderGroups()}
            </ul>
        </div>
    );
};

export default AssignGroupOverlay;
